import React from 'react';

/**
 * Premium animated world map backdrop for the Travel Tutor section.
 * - Dotted world silhouette (SVG) rendered at low opacity
 * - 10 country pins with staggered pulse animation
 * - Faint arcs connecting London to each international destination
 * All strictly in navy/gold/cream palette.
 */

// Rough lat/long → SVG (0..1000 wide, 0..500 tall) conversion for premium editorial style
// Coords calibrated for the dotted map below.
const pins = [
  { name: 'London (Home)', x: 470, y: 155, isHome: true },
  { name: 'Thailand', x: 745, y: 265 },
  { name: 'Dubai (UAE)', x: 605, y: 235 },
  { name: 'Poland (Toruń)', x: 520, y: 155 },
  { name: 'Greece', x: 535, y: 195 },
  { name: 'Albania', x: 520, y: 190 },
  { name: 'Portugal (Madeira)', x: 415, y: 215 },
  { name: 'South Korea (Daegu)', x: 800, y: 200 },
  { name: 'Sweden (Gothenburg)', x: 500, y: 115 },
  { name: 'Italy (Pescara)', x: 500, y: 190 },
  { name: 'France', x: 480, y: 175 }
];

const LONDON = pins.find(p => p.isHome);

// Dotted world map — array of {x, y} points that trace the continents at low resolution.
// Generated to feel editorial rather than geographically precise.
const worldDots = [
  // North America
  [80,140],[100,130],[120,125],[140,120],[160,125],[180,130],[200,140],[220,150],
  [90,160],[110,155],[130,150],[150,150],[170,155],[190,160],[210,170],[230,175],
  [110,180],[130,175],[150,175],[170,180],[190,185],[210,195],[230,200],
  [140,205],[160,205],[180,210],[200,215],[220,220],
  [170,235],[190,235],[210,240],
  // Central America
  [190,255],[205,260],[220,265],
  // South America
  [230,285],[245,295],[260,310],[275,325],[280,345],[275,365],[265,385],[250,400],
  [245,275],[260,285],[280,300],[295,315],[300,335],[295,355],[285,375],
  // Europe
  [460,140],[475,135],[490,135],[505,135],[520,135],[535,140],[550,145],
  [455,155],[470,155],[485,155],[500,155],[515,155],[530,160],[545,165],
  [465,175],[480,175],[495,175],[510,175],[525,180],
  [475,195],[490,195],[505,195],[520,195],
  // Africa
  [475,220],[490,220],[505,225],[520,230],[535,235],[550,240],[565,245],
  [485,245],[500,250],[515,255],[530,260],[545,265],[560,270],
  [495,275],[510,285],[525,295],[540,305],
  [510,315],[525,325],[540,335],
  [520,345],[535,355],
  // Middle East / Arabia
  [560,215],[575,220],[590,225],[605,230],[620,235],[635,240],[600,250],[615,255],
  // Asia (broad brush)
  [580,145],[600,140],[620,135],[640,135],[660,135],[680,140],[700,145],[720,150],[740,155],[760,160],[780,165],[800,170],
  [590,165],[610,160],[630,160],[650,160],[670,165],[690,170],[710,175],[730,180],[750,185],[770,190],[790,195],
  [615,185],[635,185],[655,190],[675,195],[695,200],[715,205],[735,210],[755,215],[775,220],
  [645,215],[665,220],[685,225],[705,230],[725,235],[745,240],
  [665,245],[685,250],[705,255],[725,260],
  [685,275],[705,280],[725,285],
  // India
  [660,225],[675,235],[685,245],[695,255],[680,265],
  // Southeast Asia / Indonesia
  [740,270],[755,275],[770,280],[785,285],[800,290],[815,295],[830,295],[845,290],
  [750,305],[770,305],[790,305],
  // Australia
  [820,340],[840,340],[860,340],[880,340],[900,345],
  [810,360],[830,360],[850,360],[870,360],[890,365],[910,370],
  [830,380],[850,380],[870,380],[890,385]
];

const arcPath = (start, end) => {
  // Quadratic Bézier arcing upward for a premium travel-route feel
  const mx = (start.x + end.x) / 2;
  const my = Math.min(start.y, end.y) - Math.abs(end.x - start.x) * 0.18 - 20;
  return `M ${start.x} ${start.y} Q ${mx} ${my} ${end.x} ${end.y}`;
};

const WorldMapBackdrop = () => {
  const routes = pins.filter(p => !p.isHome);

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}
    >
      <svg
        viewBox="0 0 1000 500"
        preserveAspectRatio="xMidYMid slice"
        style={{ width: '100%', height: '100%', maxWidth: '1400px', opacity: 0.9 }}
      >
        <defs>
          <radialGradient id="pinGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#C5A059" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#C5A059" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Dotted world silhouette */}
        {worldDots.map((d, i) => (
          <circle
            key={`d-${i}`}
            cx={d[0]}
            cy={d[1]}
            r="1.6"
            fill="#C5A059"
            opacity="0.15"
          />
        ))}

        {/* Travel arcs */}
        {routes.map((r, i) => (
          <path
            key={`arc-${i}`}
            d={arcPath(LONDON, r)}
            stroke="#C5A059"
            strokeWidth="0.7"
            fill="none"
            opacity="0.25"
            strokeDasharray="4 6"
            style={{
              animation: `arcDash 12s linear infinite`,
              animationDelay: `${i * 0.3}s`
            }}
          />
        ))}

        {/* Destination pins */}
        {routes.map((p, i) => (
          <g key={`p-${i}`}>
            <circle cx={p.x} cy={p.y} r="10" fill="url(#pinGlow)"
              style={{ animation: `pinPulse 3.6s ease-in-out infinite`, animationDelay: `${(i * 0.28) % 3.6}s`, transformOrigin: `${p.x}px ${p.y}px` }}
            />
            <circle cx={p.x} cy={p.y} r="2.4" fill="#C5A059" opacity="0.95" />
          </g>
        ))}

        {/* Home base — London */}
        <g>
          <circle cx={LONDON.x} cy={LONDON.y} r="16" fill="url(#pinGlow)" opacity="0.6"
            style={{ animation: `homePulse 2.6s ease-in-out infinite`, transformOrigin: `${LONDON.x}px ${LONDON.y}px` }}
          />
          <circle cx={LONDON.x} cy={LONDON.y} r="4.5" fill="#C5A059" />
          <circle cx={LONDON.x} cy={LONDON.y} r="2" fill="#FFFCF5" />
        </g>

        <style>{`
          @keyframes arcDash {
            from { stroke-dashoffset: 100; }
            to { stroke-dashoffset: 0; }
          }
          @keyframes pinPulse {
            0%, 100% { opacity: 0.35; transform: scale(0.85); }
            50% { opacity: 0.9; transform: scale(1.25); }
          }
          @keyframes homePulse {
            0%, 100% { opacity: 0.4; transform: scale(0.9); }
            50% { opacity: 0.85; transform: scale(1.15); }
          }
        `}</style>
      </svg>
    </div>
  );
};

export default WorldMapBackdrop;
